import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Plus, Eye, Trash2, RotateCw, X } from "lucide-react";
import { apiGet, protectedPost, protectedDelete, vaultReveal } from "@/lib/api";
import { cn } from "@/lib/utils";
import { ModulePage, Label, INPUT, BTN_PRIMARY, BTN_GHOST, BTN_DANGER, Empty, fmtTime } from "@/components/common/ModuleShell";

const KINDS = ["api_key", "password", "ssh_key", "token", "other"];
const blank = { name: "", kind: "api_key", value: "", note: "", rotation_days: "" };

function VaultForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || blank);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { ...form, rotation_days: form.rotation_days ? Number(form.rotation_days) : null, value: form.value || null };
      await protectedPost(initial?.id ? `/vault/${initial.id}` : "/vault", body);
      toast.success(initial?.id ? "Vault item updated." : "Stored encrypted in the vault.");
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="jv-panel p-5 grid gap-3 md:grid-cols-2" data-testid="vault-form">
      <div><Label>Name</Label><input data-testid="vault-name" className={INPUT} value={form.name} onChange={set("name")} required maxLength={80} /></div>
      <div><Label>Kind</Label>
        <select data-testid="vault-kind" className={INPUT} value={form.kind} onChange={set("kind")}>{KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</select></div>
      <div className="md:col-span-2"><Label>{initial?.id ? "New secret value (blank keeps current)" : "Secret value"}</Label>
        <input data-testid="vault-value" type="password" className={INPUT} value={form.value} onChange={set("value")} required={!initial?.id} autoComplete="off" /></div>
      <div><Label>Rotation (days, optional)</Label><input data-testid="vault-rotation" type="number" min={1} max={365} className={INPUT} value={form.rotation_days ?? ""} onChange={set("rotation_days")} /></div>
      <div><Label>Note</Label><input data-testid="vault-note" className={INPUT} value={form.note} onChange={set("note")} maxLength={300} /></div>
      <div className="md:col-span-2 flex gap-2">
        <button type="submit" disabled={busy} className={BTN_PRIMARY} data-testid="vault-save">{initial?.id ? "Save changes" : "Store encrypted"}</button>
        <button type="button" onClick={onCancel} className={BTN_GHOST} data-testid="vault-cancel">Cancel</button>
      </div>
    </form>
  );
}

function RevealDialog({ item, onClose }) {
  const [code, setCode] = useState(""), [value, setValue] = useState(null), [busy, setBusy] = useState(false), [left, setLeft] = useState(30);
  useEffect(() => {
    if (value == null) return;
    const id = setInterval(() => setLeft((n) => { if (n <= 1) { clearInterval(id); onClose(); } return n - 1; }), 1000);
    return () => clearInterval(id);
  }, [value, onClose]);
  const reveal = async (e) => {
    e.preventDefault(); setBusy(true);
    try { setValue((await vaultReveal(item.id, { code })).value); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="vault-reveal-dialog">
      <div className="jv-panel p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-jarvis-cyanBright">JIT access · {item.name}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200" data-testid="vault-reveal-close"><X className="w-4 h-4" /></button>
        </div>
        {value == null ? (
          <form onSubmit={reveal} className="space-y-3">
            <p className="text-xs text-slate-400">Enter a fresh authenticator code. The secret is shown for 30 seconds and the access is logged.</p>
            <input data-testid="vault-reveal-code" className={INPUT} inputMode="numeric" autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" />
            <button type="submit" disabled={busy || code.length !== 6} className={BTN_PRIMARY} data-testid="vault-reveal-submit">Reveal</button>
          </form>
        ) : (
          <div>
            <pre className="bg-black/60 border border-jarvis-cyan/20 rounded-md p-3 text-sm font-code text-jarvis-cyanBright break-all whitespace-pre-wrap select-all" data-testid="vault-reveal-value">{value}</pre>
            <p className="mt-2 font-mono text-[10px] text-jarvis-amber" data-testid="vault-reveal-timer">Hides in {left}s</p>
          </div>
        )}
      </div>
    </div>
  );
}

function VaultRow({ item, onReveal, onEdit, onDelete }) {
  return (
    <div className={cn("jv-panel p-4 flex flex-wrap items-center gap-3", item.rotation_overdue && "border-jarvis-amber/40")} data-testid={`vault-item-${item.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2"><span className="font-ui text-base text-slate-100">{item.name}</span>
          <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-white/10 text-slate-400">{item.kind}</span>
          {item.rotation_overdue && <span className="font-mono text-[9px] uppercase text-jarvis-amber" data-testid={`vault-overdue-${item.id}`}>Rotation overdue</span>}</div>
        <div className="font-code text-[11px] text-jarvis-cyan/70 mt-0.5 select-all">{item.ref}</div>
        <div className="font-mono text-[10px] text-slate-500 mt-1">Updated {fmtTime(item.updated_at)} · Accessed {item.access_count}× {item.last_accessed ? `· last ${fmtTime(item.last_accessed)}` : ""}{item.rotation_due ? ` · Rotate by ${fmtTime(item.rotation_due)}` : ""}</div>
        {item.note && <div className="text-xs text-slate-400 mt-1">{item.note}</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onReveal(item)} className={BTN_GHOST} data-testid={`vault-reveal-${item.id}`}><Eye className="w-3.5 h-3.5" />Reveal</button>
        <button onClick={() => onEdit(item)} className={BTN_GHOST} data-testid={`vault-edit-${item.id}`}><RotateCw className="w-3.5 h-3.5" />Rotate</button>
        <button onClick={() => onDelete(item)} className={BTN_DANGER} data-testid={`vault-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

export default function VaultPage() {
  const [items, setItems] = useState(null), [editing, setEditing] = useState(null), [reveal, setReveal] = useState(null);
  const load = useCallback(() => apiGet("/vault").then((r) => setItems(r.items)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the vault? This cannot be undone.`)) return;
    try { await protectedDelete(`/vault/${item.id}`); toast.success("Deleted."); load(); } catch (e) { toast.error(e.message); }
  };
  const overdue = items?.filter((i) => i.rotation_overdue).length || 0;
  return (
    <ModulePage icon={KeyRound} title="Vault" testId="vault-page"
      subtitle={items ? `${items.length} encrypted item${items.length === 1 ? "" : "s"} · ${overdue} rotation${overdue === 1 ? "" : "s"} overdue · JIT reveal with authenticator` : "Loading…"}
      right={!editing && <button onClick={() => setEditing(blank)} className={BTN_PRIMARY} data-testid="vault-add"><Plus className="w-3.5 h-3.5" />Add credential</button>}>
      {editing && <div className="mb-5"><VaultForm initial={editing.id ? { ...editing, value: "" } : null} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} /></div>}
      <div className="space-y-3">
        {items?.length === 0 && <Empty testId="vault-empty">Vault is empty. Store API keys, SSH keys and passwords here — encrypted at rest, revealed only with a fresh authenticator code.</Empty>}
        {items?.map((item) => <VaultRow key={item.id} item={item} onReveal={setReveal} onEdit={setEditing} onDelete={remove} />)}
      </div>
      <p className="mt-6 font-mono text-[10px] text-slate-600 tracking-wider">AI provider keys are managed in Settings. Reference vault items in automations as <span className="text-jarvis-cyan/70">vault://name</span>.</p>
      {reveal && <RevealDialog item={reveal} onClose={() => { setReveal(null); load(); }} />}
    </ModulePage>
  );
}
